import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { BsInfoCircle } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { Group, GroupService, User, UserService } from '../../../../api';
import { groupQueryKeys } from '../../../../app/query-keys-factories/groups';
import { userQueryKeys } from '../../../../app/query-keys-factories/user';
import { handleApiError } from '../../../../app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../../../../utils/forms';

interface EditUserGroupsDialogProps {
  user: User;
  show: boolean;
  onClose: () => void;
}

export const EditUserGroupsDialog = ({ user, show, onClose }: EditUserGroupsDialogProps) => {
  const form = useForm();

  const queryGroups = useQuery({
    queryKey: groupQueryKeys.list({}),
    queryFn: async () => {
      let groups: Group[] = [];
      const limit = 100;
      let totalCount: number;
      let offset = 0;

      // fetch all facilities considering that data.count is the total number of facilities
      let data = await GroupService.groupList({
        limit,
        offset,
      });
      totalCount = data.count as number;
      groups = groups.concat(data.results || []);
      while (groups.length < totalCount) {
        offset += limit;
        data = await GroupService.groupList({ limit, offset });
        totalCount = data.count as number;
        groups = groups.concat(data.results || []);
      }
      return groups;
    },
    keepPreviousData: true,
  });

  useEffect(() => {
    if (
      !user.group_names?.length ||
      (user.group_names.length === 1 && user.group_names[0] === null)
    ) {
      form.setValue('group_names', false);
    } else {
      form.setValue('group_names', user.group_names);
    }
  }, [user.group_names]);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: { group_names: string[] }) => {
      await UserService.userPartialUpdate({ id: user.id, requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userQueryKeys.all());
    },
  });

  const onSubmit = async (data) => {
    let submitData = data;
    if (typeof data.group_names === 'boolean') {
      submitData = { group_names: [] };
    }
    if (typeof data.group_names === 'string') {
      submitData = { group_names: [data.group_names] };
    }

    try {
      await mutation.mutateAsync(submitData);
      toast.success('User permissions updated successfully.');
      onClose();
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, form.setError);
      handleApiError(errors);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Form onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User Permissions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <p className="mt-1 bg-primary-muted border-1 tw:border-zinc-100 p-2 text-muted small rounded fw-semibold">
              <BsInfoCircle className="tw:inline me-2" size="15px" />
              By default, every team member has access to view the marketplace and connected
              providers.
            </p>
            <p className="mb-0">
              Set the appropriate permissions for{' '}
              <b>
                {user.first_name} {user.last_name}
              </b>
            </p>
          </div>
          {queryGroups.isLoading && <div>Loading...</div>}
          {queryGroups.isError && <div>{JSON.stringify(queryGroups.error)}</div>}
          {queryGroups.isSuccess && (
            <div>
              {queryGroups.data?.map((group) => (
                <Form.Group controlId={`group_names-${group.code}`} key={group.code}>
                  <Form.Check
                    type="switch"
                    value={group.code}
                    label={group.title}
                    {...form.register('group_names')}
                  />
                  <div className="tw:ml-[2.5rem] tw:text-xs tw:text-zinc-500 tw:italic tw:mb-[.6rem]">
                    {group.description}
                  </div>
                </Form.Group>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" type="submit">
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
